import { z } from 'zod';

export const supportedLanguages = ['nl', 'fr', 'en'] as const;
export const languageSchema = z.enum(supportedLanguages);
export type Language = z.infer<typeof languageSchema>;

/**
 * Wachtwoordbeleid: twaalf tekens, en niet iets wat op elke lijst met veelgebruikte
 * wachtwoorden staat. Bewust geen eis voor hoofdletters en leestekens — dat levert
 * `Welkom2026!` op, en dat is geen sterker wachtwoord dan een lange zin.
 */
export const passwordSchema = z
  .string()
  .min(12, 'Gebruik minstens 12 tekens.')
  .max(200, 'Gebruik hoogstens 200 tekens.');

export const pinSchema = z.string().regex(/^\d{6}$/, 'Een pincode bestaat uit 6 cijfers.');

/** Inloggen als gebruiker binnen een tenant: bedrijfscode + e-mail + wachtwoord. */
export const loginRequestSchema = z.object({
  /**
   * Optioneel sinds ACC-1: zonder bedrijfscode zoekt de server elk account met dit e-mailadres.
   * Met code werkt het oude pad zoals voorheen (demo-script, e2e, bestaande clients).
   */
  tenantCode: z.string().min(2).max(32).optional(),
  email: z.string().trim().email().max(320),
  password: z.string().min(1).max(200),
  /** De tweede stap (FBK-12): de code uit de authenticator-app, of een herstelcode. */
  totpCode: z
    .string()
    .regex(/^\d{6}$/, 'Een verificatiecode bestaat uit 6 cijfers.')
    .optional(),
  recoveryCode: z.string().trim().min(8).max(20).optional(),
  deviceLabel: z.string().max(100).optional(),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

/**
 * Activeren zonder e-mailadres: bedrijfscode, personeelsnummer en de code van de brief.
 *
 * Veel productiemedewerkers hebben geen bedrijfse-mail en willen hun privéadres niet
 * geven. Dit is voor hen de enige weg naar binnen, dus hij moet op een telefoon met één
 * hand te doen zijn.
 */
export const activationCodeSchema = z
  .string()
  .min(10)
  .max(14)
  .transform((value) => value.toUpperCase().replace(/[^A-Z0-9]/g, ''));

export const activateRequestSchema = z.object({
  tenantCode: z.string().min(2).max(32),
  employeeNumber: z.string().min(1).max(32),
  activationCode: activationCodeSchema,
  pin: pinSchema,
  deviceLabel: z.string().max(100).optional(),
});
export type ActivateRequest = z.infer<typeof activateRequestSchema>;

export const pinLoginRequestSchema = z.object({
  tenantCode: z.string().min(2).max(32),
  employeeNumber: z.string().min(1).max(32),
  pin: z.string().min(1).max(20),
  deviceLabel: z.string().max(100).optional(),
});
export type PinLoginRequest = z.infer<typeof pinLoginRequestSchema>;

export const changePinRequestSchema = z.object({
  currentPin: z.string().min(1).max(20),
  newPin: pinSchema,
});
export type ChangePinRequest = z.infer<typeof changePinRequestSchema>;

/**
 * De activatiecode wordt hier **één keer** teruggegeven, om af te drukken of voor te
 * lezen. Daarna bestaat hij alleen nog gehasht; opnieuw opvragen betekent een nieuwe code
 * maken, en dat maakt de oude ongeldig.
 */
export const activationCodeResponseSchema = z.object({
  personId: z.guid(),
  employeeNumber: z.string().nullable(),
  displayName: z.string(),
  code: z.string(),
  expiresAt: z.string().datetime(),
});
export type ActivationCodeResponse = z.infer<typeof activationCodeResponseSchema>;

export const accountStatusSchema = z.enum(['none', 'pending_activation', 'active', 'disabled']);
export type AccountStatus = z.infer<typeof accountStatusSchema>;

/** Wat HR in de kolom "Activatie" en op de tab "Toegang" ziet. */
export const accessStatusSchema = z.object({
  personId: z.guid(),
  status: accountStatusSchema,
  hasPin: z.boolean(),
  lastLoginAt: z.string().datetime().nullable(),
  lockedUntil: z.string().datetime().nullable(),
  activationCodeExpiresAt: z.string().datetime().nullable(),
});
export type AccessStatus = z.infer<typeof accessStatusSchema>;

/**
 * Hoeveel accounts er in dit bedrijf werkelijk in gebruik zijn (Extra 2, punt 8).
 *
 * De tegenhanger van `peopleStatsSchema` in `directory`. Twee endpoints en geen gezamenlijk: de
 * module die de gegevens bezit, telt ze (ADR-0016). Het startscherm legt de twee naast elkaar.
 */
export const accountStatsSchema = z.object({
  /** Een pincode gekozen en kunnen aanmelden: dit is de teller van de activatiegraad. */
  activated: z.number().int(),
  /** Een account met een code die nog niet gebruikt is. */
  pendingActivation: z.number().int(),
});
export type AccountStats = z.infer<typeof accountStatsSchema>;

export const accessStatusListSchema = z.object({ items: z.array(accessStatusSchema) });
export type AccessStatusList = z.infer<typeof accessStatusListSchema>;

/**
 * Inloggen als platformbeheerder van UP2D8. De TOTP-code is verplicht zodra er een
 * MFA-geheim is ingesteld; de client stuurt hem in dezelfde aanvraag na de eerste
 * `MFA_REQUIRED`.
 */
export const platformLoginRequestSchema = z.object({
  email: z.string().email().max(320),
  password: z.string().min(1).max(200),
  totpCode: z
    .string()
    .regex(/^\d{6}$/, 'Een verificatiecode bestaat uit 6 cijfers.')
    .optional(),
  recoveryCode: z.string().trim().min(8).max(20).optional(),
  deviceLabel: z.string().max(100).optional(),
});
export type PlatformLoginRequest = z.infer<typeof platformLoginRequestSchema>;

export const tokenResponseSchema = z.object({
  accessToken: z.string(),
  expiresIn: z.number().int(),
  tokenType: z.literal('Bearer'),
});
export type TokenResponse = z.infer<typeof tokenResponseSchema>;

// --- Aanmelden met e-mail en wachtwoord, daarna kiezen (ACC-1, ADR-0080) ---------------------

/** Een bedrijf waarin dit e-mailadres een account heeft. */
export const tenantChoiceSchema = z.object({
  tenantCode: z.string(),
  name: z.string(),
});
export type TenantChoice = z.infer<typeof tenantChoiceSchema>;

/**
 * Het antwoord van `POST /auth/login`: een sessie, of - bij meer dan één bedrijf - de keuze. De
 * lijst komt pas na een geslaagd wachtwoord; het keuzetoken werkt één keer en kort.
 */
export const loginResponseSchema = z.object({
  accessToken: z.string().optional(),
  expiresIn: z.number().int().optional(),
  tokenType: z.literal('Bearer').optional(),
  choose: z.array(tenantChoiceSchema).optional(),
  chooseToken: z.string().optional(),
  /**
   * 2FA is verplicht maar nog niet ingesteld (FBK-12): geen sessie, wel een eenmalig token van
   * tien minuten om de authenticator-app te koppelen (`/auth/mfa/enroll/*`).
   */
  enrollToken: z.string().optional(),
});
export type LoginResponse = z.infer<typeof loginResponseSchema>;

export const loginChooseRequestSchema = z.object({
  chooseToken: z.string().min(16).max(200),
  tenantCode: z.string().min(2).max(32),
  /** De tweede stap (FBK-12): de code uit de authenticator-app, of een herstelcode. */
  totpCode: z
    .string()
    .regex(/^\d{6}$/, 'Een verificatiecode bestaat uit 6 cijfers.')
    .optional(),
  recoveryCode: z.string().trim().min(8).max(20).optional(),
  deviceLabel: z.string().max(100).optional(),
});
export type LoginChooseRequest = z.infer<typeof loginChooseRequestSchema>;

/** De bedrijven waartussen deze persoon kan wisselen zonder opnieuw aan te melden. */
export const myTenantsSchema = z.object({
  items: z.array(tenantChoiceSchema.extend({ current: z.boolean() })),
});
export type MyTenants = z.infer<typeof myTenantsSchema>;

export const switchTenantRequestSchema = z.object({
  tenantCode: z.string().min(2).max(32),
});
export type SwitchTenantRequest = z.infer<typeof switchTenantRequestSchema>;

export const audienceSchema = z.enum(['tenant', 'platform']);
export type TokenAudience = z.infer<typeof audienceSchema>;

export const meResponseSchema = z.object({
  accountId: z.guid(),
  personId: z.guid().nullable(),
  tenantId: z.guid().nullable(),
  tenantCode: z.string().nullable(),
  email: z.string().email(),
  displayName: z.string(),
  audience: audienceSchema,
  preferredLanguage: languageSchema,
  /**
   * Deze persoon kan ook bij Master (MST-10): een platformbeheerder met hetzelfde e-mailadres. Het
   * menu toont dan "Master"; aanmelden daar blijft een eigen stap.
   */
  platformAccess: z.boolean().default(false),
});
export type MeResponse = z.infer<typeof meResponseSchema>;

export const logoutResponseSchema = z.object({ ok: z.literal(true) });
export type LogoutResponse = z.infer<typeof logoutResponseSchema>;

// --- Uitnodigingen -------------------------------------------------------------------

export const createInvitationRequestSchema = z.object({
  email: z.string().email().max(320),
  displayName: z.string().min(2).max(120),
  /** Welke rol de uitgenodigde krijgt zodra hij de uitnodiging aanvaardt. */
  roleHint: z.literal('admin').default('admin'),
  /**
   * De rol die de uitgenodigde krijgt (Extra 4).
   *
   * Leeg betekent: de rol Beheerder, zoals voorheen. Staat er een rol, dan moet de **uitnodiger**
   * die mogen toekennen én mag ze niet meer rechten bevatten dan hij zelf heeft - anders is
   * uitnodigen een manier om jezelf te promoveren via een omweg.
   *
   * `role_hint` blijft bestaan als terugval voor uitnodigingen van vóór Extra 4.
   */
  roleId: z.guid().nullable().default(null),
});
export type CreateInvitationRequest = z.infer<typeof createInvitationRequestSchema>;

/**
 * Het soort uitnodiging (Extra 4).
 *
 * Nu alleen `admin`. Stap 34 voegt `person` (een persoonlijke uitnodiging voor een medewerker) en
 * `open` (een link of QR-code die meermaals gebruikt mag worden) toe. De naam en het type komen
 * uit die stap, zodat ze er alleen waarden aan hoeft toe te voegen.
 */
export const invitationKindSchema = z.enum(['admin']);
export type InvitationKind = z.infer<typeof invitationKindSchema>;

/**
 * Waar een uitnodiging in haar leven staat.
 *
 * Afgeleid en niet opgeslagen: `open` is "nog geldig", en dat hangt van de klok af. Een kolom
 * `status` zou elke nacht een job nodig hebben om "verlopen" te worden - en tussen middernacht en
 * die job zou het scherm iets anders zeggen dan de server.
 */
export const invitationStatusSchema = z.enum(['open', 'expired', 'revoked', 'accepted']);
export type InvitationStatus = z.infer<typeof invitationStatusSchema>;

export const invitationSchema = z.object({
  id: z.guid(),
  tenantId: z.guid(),
  email: z.string().email(),
  displayName: z.string(),
  expiresAt: z.string().datetime(),
  acceptedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  kind: invitationKindSchema.default('admin'),
  status: invitationStatusSchema.default('open'),
  revokedAt: z.string().datetime().nullable().default(null),
  /** De rol die de uitgenodigde krijgt; leeg betekent de rol Beheerder. */
  roleId: z.guid().nullable().default(null),
  /** De naam van de rol in het Nederlands, of in de eerste taal die ingevuld is. */
  roleName: z.string().nullable().default(null),
  /**
   * De naam van de rol per taal: het scherm toont die van de lezer. `roleName` alleen was voor een
   * Nederlandstalige beheerder vaak Engels ("Employee"), want de server kent de taal van het
   * scherm niet.
   */
  roleNames: z.partialRecord(languageSchema, z.string()).nullable().default(null),
  /** Of de mail vertrok (ACC-2). `failed`: "Mail niet verzonden", met "Opnieuw proberen". */
  mailStatus: z.enum(['sent', 'failed']).default('sent'),
  /**
   * Of UP2D8 zelf uitnodigde, of het bedrijf.
   *
   * Staat op het scherm van de platformbeheerder: wie zich afvraagt hoe iemand binnenkwam, hoort
   * dat te kunnen zien zonder in een logboek te duiken.
   */
  invitedByKind: z.enum(['platform', 'tenant']).default('platform'),
  /** De naam van wie uitnodigde, als die bekend is. */
  invitedByName: z.string().nullable().default(null),
});
export type Invitation = z.infer<typeof invitationSchema>;

export const invitationListSchema = z.object({ items: z.array(invitationSchema) });
export type InvitationList = z.infer<typeof invitationListSchema>;

export const invitationQuerySchema = z.object({
  kind: invitationKindSchema.default('admin'),
  /** Leeg betekent alles; het scherm vraagt per sectie wat het nodig heeft. */
  status: invitationStatusSchema.optional(),
});
export type InvitationQuery = z.input<typeof invitationQuerySchema>;

/**
 * Een huidige beheerder van dit bedrijf (Extra 4).
 *
 * Dit komt uit `identity` en niet uit `access`, en dat is een keuze: de **rij** gaat over een
 * account (naam, e-mail, laatste aanmelding) en dat bezit `identity`. Welke rollen dat account
 * heeft, vraagt `identity` aan `access` via de facade. Andersom zou `access` accountgegevens
 * moeten teruggeven die het niet bezit.
 */
export const tenantAdminSchema = z.object({
  personId: z.guid(),
  displayName: z.string(),
  email: z.string().nullable(),
  /** De rollen waarmee deze persoon beheerder is; meestal één. In het Nederlands. */
  roles: z.array(z.string()),
  /**
   * Dezelfde rollen per taal, in dezelfde volgorde: het scherm toont die van de lezer. `roles`
   * alleen was vaak Engels ("Administrator"), want de server kent de taal van het scherm niet.
   */
  roleNames: z.array(z.partialRecord(languageSchema, z.string())).default([]),
  lastLoginAt: z.string().datetime().nullable(),
});
export type TenantAdmin = z.infer<typeof tenantAdminSchema>;

export const tenantAdminListSchema = z.object({ items: z.array(tenantAdminSchema) });
export type TenantAdminList = z.infer<typeof tenantAdminListSchema>;

/** Wat de uitgenodigde ziet vóór hij een wachtwoord kiest: klopt de uitnodiging nog? */
export const invitationPreviewSchema = z.object({
  email: z.string().email(),
  displayName: z.string(),
  tenantName: z.string(),
  tenantCode: z.string(),
  /**
   * Dit adres heeft al een account in een ander bedrijf (ACC-2): dan bevestigt de persoon met zijn
   * bestaande wachtwoord in plaats van er een nieuw te verzinnen.
   */
  existingAccount: z.boolean().default(false),
});
export type InvitationPreview = z.infer<typeof invitationPreviewSchema>;

export const acceptInvitationRequestSchema = z.object({
  token: z.string().min(20).max(200),
  password: passwordSchema,
});
export type AcceptInvitationRequest = z.infer<typeof acceptInvitationRequestSchema>;

/** Toegang bevestigen met het wachtwoord van een bestaand account (ACC-2). */
export const confirmInvitationRequestSchema = z.object({
  token: z.string().min(20).max(200),
  password: z.string().min(1).max(200),
});
export type ConfirmInvitationRequest = z.infer<typeof confirmInvitationRequestSchema>;

// --- Wachtwoord ----------------------------------------------------------------------

export const forgotPasswordRequestSchema = z.object({
  tenantCode: z.string().min(2).max(32),
  email: z.string().email().max(320),
});
export type ForgotPasswordRequest = z.infer<typeof forgotPasswordRequestSchema>;

export const resetPasswordRequestSchema = z.object({
  token: z.string().min(20).max(200),
  password: passwordSchema,
});
export type ResetPasswordRequest = z.infer<typeof resetPasswordRequestSchema>;

export const changePasswordRequestSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: passwordSchema,
});
export type ChangePasswordRequest = z.infer<typeof changePasswordRequestSchema>;

export const acceptedResponseSchema = z.object({ accepted: z.literal(true) });
export type AcceptedResponse = z.infer<typeof acceptedResponseSchema>;

export * from './registration';
export * from './kiosk';
export * from './platform-accounts';
export * from './account-deletion';
export * from './member-invitations';

// --- Tweestapsverificatie (FBK-12, ADR-0095) ------------------------------------------------

/** Een geheim om in de authenticator-app te zetten: als QR-code, als link en als tekst. */
export const mfaSetupSchema = z.object({
  /** Base32, om met de hand in te tikken als scannen niet lukt. */
  secret: z.string(),
  otpauthUri: z.string(),
  /** De QR-code als SVG; de server maakt hem, zodat het geheim de app niet hoeft te verlaten. */
  qrSvg: z.string(),
});
export type MfaSetup = z.infer<typeof mfaSetupSchema>;

export const mfaEnrollTokenRequestSchema = z.object({ enrollToken: z.string().min(16).max(200) });
export type MfaEnrollTokenRequest = z.infer<typeof mfaEnrollTokenRequestSchema>;

export const mfaCodeSchema = z
  .string()
  .regex(/^\d{6}$/, 'Een verificatiecode bestaat uit 6 cijfers.');

export const mfaEnrollConfirmRequestSchema = mfaEnrollTokenRequestSchema.extend({
  code: mfaCodeSchema,
});
export type MfaEnrollConfirmRequest = z.infer<typeof mfaEnrollConfirmRequestSchema>;

/** De herstelcodes, één keer getoond; daarna bestaan ze alleen nog als hash. */
export const mfaRecoveryCodesSchema = z.object({ recoveryCodes: z.array(z.string()) });
export type MfaRecoveryCodes = z.infer<typeof mfaRecoveryCodesSchema>;

export const mfaEnrollConfirmResponseSchema = tokenResponseSchema
  .merge(mfaRecoveryCodesSchema)
  .extend({
    audience: z.enum(['tenant', 'platform']),
  });
export type MfaEnrollConfirmResponse = z.infer<typeof mfaEnrollConfirmResponseSchema>;

export const mfaStatusSchema = z.object({
  enabled: z.boolean(),
  /** Het bedrijf verplicht 2FA voor beheerders, en deze persoon beheert: uitzetten mag dan niet. */
  required: z.boolean(),
  recoveryCodesLeft: z.number().int(),
});
export type MfaStatus = z.infer<typeof mfaStatusSchema>;

export const mfaCodeRequestSchema = z.object({ code: mfaCodeSchema });
export type MfaCodeRequest = z.infer<typeof mfaCodeRequestSchema>;

export const securitySettingsSchema = z.object({ mfaRequiredForAdmins: z.boolean() });
export type SecuritySettings = z.infer<typeof securitySettingsSchema>;
